import { Component, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { OtapUpgradeTaskDTO, OtapUpgradeTaskInputParametersDTO } from '@app/shared/models/otap-upgrade-task.interface';
import { getOtapTaskInputValidators, isOtapTaskInputRequired } from './otap-task-input.validator';

import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

@Component({
  selector: 'app-form-job-task',
  templateUrl: './form-job-task.component.html',
  styleUrls: ['./form-job-task.component.scss'],
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError]
})
export class FormJobTaskComponent {
  private readonly formBuilder = inject(FormBuilder);
  readonly task = input.required<OtapUpgradeTaskDTO>();
  readonly taskDataChanged = output<OtapUpgradeTaskInputParametersDTO[]>();
  readonly taskFormValidityChanged = output<boolean>();
  readonly taskForm = signal<FormGroup>(this.formBuilder.group({}));

  constructor() {
    effect(onCleanup => {
      const task = this.task();
      const taskForm = this.formBuilder.group({});
      task.inputParameters.forEach(parameter => {
        taskForm.addControl(parameter.name, this.formBuilder.control(parameter.value, getOtapTaskInputValidators(parameter, task.taskType)));
      });
      this.taskForm.set(taskForm);
      this.taskFormValidityChanged.emit(taskForm.valid);

      const subscription = taskForm.valueChanges.subscribe(changedForm => {
        const changedParameters = task.inputParameters.map(param => {
        const formValue = changedForm[param.name];
        return { ...param, value: formValue };
      });
      this.taskDataChanged.emit(changedParameters);
      this.taskFormValidityChanged.emit(taskForm.valid);
      });
      onCleanup(() => subscription.unsubscribe());
    });
  }

  isRequired(parameter: OtapUpgradeTaskInputParametersDTO): boolean {
    return isOtapTaskInputRequired(parameter, this.task().taskType);
  }

  inputPlaceholder(parameter: OtapUpgradeTaskInputParametersDTO): string {
    switch (parameter.name) {
      case 'SOURCE_ENDPOINT':
      case 'DESTINATION_ENDPOINT':
        return '0-255';
      case 'DESTINATION':
        return 'IDs, space-separated';
      case 'DESTINATION_HEX':
        return 'Hex IDs, space-separated';
      case 'URL':
        return 'http(s) URL';
      case 'HEX_COMMAND':
        return 'Even-length hex';
      case 'SEQ':
        return [
          'INITIALIZE_PIC_OTAP',
          'START_UPDATE_COMMAND',
          'SEND_NORDIC_UPDATE',
          'QUERY_NORDIC_SCRATCHPAD_STATUS'
        ].includes(this.task().taskType) ? '0-255' : 'auto or 0-255';
      case 'CRC':
        return 'CRC 0-65535';
      case 'DELAY':
        return 'PIC delay 0-255';
      case 'TIME_OUT':
        return 'Seconds, numeric';
      case 'TIME_OUT_ITERATIONS':
        return 'Attempt count';
      case 'ACTION':
        return '1 or 2';
      case 'ACTION_DELAY':
        return 'Delay seconds';
      default:
        return '';
    }
  }
}
